import { Box } from "@mui/material";
import type { EditorOptions } from "@tiptap/core";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
  useState,
  forwardRef,
} from "react";
import {
  LinkBubbleMenu,
  RichTextEditor,
  insertImages,
  type RichTextEditorRef,
} from "mui-tiptap";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";
import { ROWENA_VARIABLE_MIME, ROWENA_VARIABLE_NAMES } from "./rowenaVariables";
import { DEFAULT_FORMAT } from "./rowenaFormat";
import MentionFormatPopover from "./MentionFormatPopover";
import ConditionalPopover from "./ConditionalPopover";
import { RowenaConditionalAttrs } from "./rowenaConditional";
import {
  RowenaAvailableVariable,
  RowenaVariableFormat,
} from "types/api/SystemTenant/RowenaAI/RowenaContentTypes";

function fileListToImageFiles(fileList: FileList): File[] {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}

interface EditorProps {
  options?: EditorOptions;
  content: string;
  variables?: Record<string, RowenaAvailableVariable>;
}

interface MentionPopoverState {
  pos: number;
  name: string;
  format: string;
  controlledBy: string | null;
  option: string | null;
  linkText: string | null;
}

interface ConditionalPopoverState {
  pos: number;
  attrs: RowenaConditionalAttrs;
}

const EMPTY_RECT: DOMRect =
  typeof DOMRect !== "undefined"
    ? new DOMRect(0, 0, 0, 0)
    : ({
        x: 0,
        y: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: 0,
        height: 0,
        toJSON: () => ({}),
      } as DOMRect);

const Editor = forwardRef<RichTextEditorRef, EditorProps>(
  ({ options, content, variables }, ref) => {
    const extensions = useExtensions({
      placeholder: "Add your own content here...",
    });
    const rteRef = useRef<RichTextEditorRef>(null);
    const variablesRef = useRef(variables);
    variablesRef.current = variables;
    const [popoverState, setPopoverState] =
      useState<MentionPopoverState | null>(null);
    const [conditionalState, setConditionalState] =
      useState<ConditionalPopoverState | null>(null);

    const handleNewImageFiles = useCallback(
      (files: File[], insertPosition?: number): void => {
        if (!rteRef.current?.editor) {
          return;
        }

        const attributesForImageFiles = files.map((file) => ({
          src: URL.createObjectURL(file),
          alt: file.name,
        }));

        insertImages({
          images: attributesForImageFiles,
          editor: rteRef.current.editor,
          position: insertPosition,
        });
      },
      []
    );

    // Allow for dropping images and Rowena variable chips into the editor
    const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
      useCallback(
        (view, event, _slice, _moved) => {
          if (!(event instanceof DragEvent) || !event.dataTransfer) {
            return false;
          }

          const variableName = event.dataTransfer.getData(ROWENA_VARIABLE_MIME);
          if (variableName && ROWENA_VARIABLE_NAMES.includes(variableName)) {
            const editor = rteRef.current?.editor;
            if (!editor) return false;

            const dropPos = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })?.pos;
            if (dropPos === undefined) return false;

            editor
              .chain()
              .focus()
              .insertContentAt(dropPos, [
                {
                  type: "mention",
                  attrs: { id: variableName, label: variableName },
                },
                { type: "text", text: " " },
              ])
              .run();

            event.preventDefault();
            return true;
          }

          const imageFiles = fileListToImageFiles(event.dataTransfer.files);
          if (imageFiles.length > 0) {
            const insertPosition = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            })?.pos;

            handleNewImageFiles(imageFiles, insertPosition);
            event.preventDefault();
            return true;
          }

          return false;
        },
        [handleNewImageFiles]
      );

    // Allow for pasting images
    const handlePaste: NonNullable<
      EditorOptions["editorProps"]["handlePaste"]
    > = useCallback(
      (_view, event, _slice) => {
        if (!event.clipboardData) {
          return false;
        }

        const pastedImageFiles = fileListToImageFiles(
          event.clipboardData.files
        );
        if (pastedImageFiles.length > 0) {
          handleNewImageFiles(pastedImageFiles);
          return true;
        }

        return false;
      },
      [handleNewImageFiles]
    );

    // Open the format popover when an inserted mention chip is clicked.
    const handleClickOn: NonNullable<
      EditorOptions["editorProps"]["handleClickOn"]
    > = useCallback((_view, _pos, node, nodePos, _event, direct) => {
      if (!direct) return false;
      if (node.type.name !== "mention") return false;

      const id = (node.attrs.id as string) ?? "";
      const label = (node.attrs.label as string) ?? "";
      // Prefer id, but fall back to label if id doesn't resolve — handles
      // legacy data where id is junk but label still references a real var.
      const liveVars = variablesRef.current;
      const resolvedName =
        id && liveVars?.[id] ? id : label && liveVars?.[label] ? label : id;
      setPopoverState({
        pos: nodePos,
        name: resolvedName,
        format: (node.attrs.format as string) ?? DEFAULT_FORMAT,
        controlledBy: (node.attrs.controlledBy as string | null) ?? null,
        option: (node.attrs.option as string | null) ?? null,
        linkText: (node.attrs.linkText as string | null) ?? null,
      });
      return false;
    }, []);

    const handleClosePopover = useCallback(() => {
      setPopoverState(null);
      rteRef.current?.editor?.commands.focus();
    }, []);

    const handleSavePopover = useCallback(
      (changes: {
        format: RowenaVariableFormat;
        controlledBy: string | null;
        option: string | null;
        linkText: string | null;
      }) => {
        const editor = rteRef.current?.editor;
        if (!editor || !popoverState) return;
        editor
          .chain()
          .setNodeSelection(popoverState.pos)
          .updateAttributes("mention", {
            format: changes.format,
            controlledBy: changes.controlledBy,
            option: changes.option,
            linkText: changes.linkText,
          })
          .run();
      },
      [popoverState]
    );

    const handleCloseConditional = useCallback(() => {
      const editor = rteRef.current?.editor;
      const pos = conditionalState?.pos;
      setConditionalState(null);
      if (!editor || pos == null) return;
      // Move cursor just after the conditional so the user can immediately type
      // in the trailing paragraph instead of being focused on the popover button.
      const condNode = editor.state.doc.nodeAt(pos);
      if (condNode) {
        const target = pos + condNode.nodeSize + 1;
        const docSize = editor.state.doc.content.size;
        editor.chain().focus(Math.min(target, docSize)).run();
      } else {
        editor.commands.focus();
      }
    }, [conditionalState]);

    const handleSaveConditional = useCallback(
      (attrs: RowenaConditionalAttrs) => {
        const editor = rteRef.current?.editor;
        if (!editor || !conditionalState) return;
        editor.commands.updateRowenaConditional(conditionalState.pos, attrs);
      },
      [conditionalState]
    );

    const handleDeleteConditional = useCallback(() => {
      const editor = rteRef.current?.editor;
      if (!editor || !conditionalState) return;
      editor.commands.unwrapRowenaConditional(conditionalState.pos);
    }, [conditionalState]);

    // Wire the storage callbacks read by the ConditionalBlock node view and the
    // mention suggestion popup. Update on variables change and force ProseMirror
    // to re-render existing mention/conditional nodes so orphan styling reflects
    // the new variables map.
    useEffect(() => {
      const editor = rteRef.current?.editor;
      if (!editor) return;
      const conditionalStorage = editor.storage.rowenaConditional;
      if (conditionalStorage) {
        conditionalStorage.variables = variables;
        conditionalStorage.onEdit = (
          pos: number,
          current: RowenaConditionalAttrs
        ) => {
          setConditionalState({ pos, attrs: current });
        };
      }
      const mentionStorage = editor.storage.mention;
      if (mentionStorage) {
        mentionStorage.variables = variables;
      }

      if (!variables) return;
      const tr = editor.state.tr;
      let touched = false;
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "mention") {
          const id = node.attrs.id as string | null | undefined;
          const label = node.attrs.label as string | null | undefined;
          const nextAttrs = { ...node.attrs };
          // Auto-heal: legacy/stale data sometimes has a junk id (e.g. "0")
          // while the label still references a real variable. Rewrite id to
          // match label so the popover, payload, and orphan check all line up.
          if ((!id || !variables[id]) && label && variables[label]) {
            nextAttrs.id = label;
          }
          tr.setNodeMarkup(pos, undefined, nextAttrs);
          touched = true;
        } else if (node.type.name === "rowenaConditional") {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs });
          touched = true;
        }
        return true;
      });
      if (touched) {
        editor.view.dispatch(tr.setMeta("addToHistory", false));
      }
    }, [variables]);

    // Virtual anchor: resolves the mention's CURRENT DOM rect on every measure.
    const virtualAnchor = useMemo(() => {
      if (!popoverState) return null;
      const pos = popoverState.pos;
      return {
        getBoundingClientRect: (): DOMRect => {
          const editor = rteRef.current?.editor;
          if (!editor) return EMPTY_RECT;
          try {
            const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
            if (dom?.getBoundingClientRect) {
              const rect = dom.getBoundingClientRect();
              if (rect.width > 0 || rect.height > 0) return rect;
            }
            const coords = editor.view.coordsAtPos(pos);
            return new DOMRect(
              coords.left,
              coords.top,
              1,
              Math.max(1, coords.bottom - coords.top)
            );
          } catch {
            return EMPTY_RECT;
          }
        },
      };
    }, [popoverState]);

    // Same approach for the conditional pill — anchor to the block's current DOM.
    const conditionalAnchor = useMemo(() => {
      if (!conditionalState) return null;
      const pos = conditionalState.pos;
      return {
        getBoundingClientRect: (): DOMRect => {
          const editor = rteRef.current?.editor;
          if (!editor) return EMPTY_RECT;
          try {
            const dom = editor.view.nodeDOM(pos) as HTMLElement | null;
            if (dom?.getBoundingClientRect) {
              const rect = dom.getBoundingClientRect();
              if (rect.width > 0 || rect.height > 0) return rect;
            }
            const coords = editor.view.coordsAtPos(pos);
            return new DOMRect(
              coords.left,
              coords.top,
              1,
              Math.max(1, coords.bottom - coords.top)
            );
          } catch {
            return EMPTY_RECT;
          }
        },
      };
    }, [conditionalState]);

    const handleInsertConditional = useCallback(() => {
      const editor = rteRef.current?.editor;
      if (!editor) return;
      editor.chain().focus().insertRowenaConditional().run();

      // After insertion, find the newly-inserted block (just before the cursor)
      // and open the popover so the author can configure it immediately.
      const { selection, doc } = editor.state;
      let inserted: { pos: number; attrs: RowenaConditionalAttrs } | null =
        null;
      doc.descendants((node, pos) => {
        if (node.type.name === "rowenaConditional" && pos < selection.from) {
          inserted = { pos, attrs: node.attrs as RowenaConditionalAttrs };
        }
        return true;
      });
      if (inserted) {
        setConditionalState(inserted);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      editor: rteRef.current?.editor || null,
    }));

    const popoverVariable = popoverState
      ? variables?.[popoverState.name]
      : undefined;

    return (
      <>
        <Box
          sx={{
            "& .ProseMirror": {
              "& h1, & h2, & h3, & h4, & h5, & h6": {
                scrollMarginTop: 50,
              },
              "& .rowena-mention": {
                cursor: "pointer",
              },
              '& .rowena-mention[data-orphan="true"]': {
                color: "#D32F2F",
                textDecoration: "underline dashed",
                textDecorationColor: "#D32F2F",
              },
            },
          }}
        >
          <RichTextEditor
            ref={rteRef}
            extensions={extensions}
            content={content}
            editable
            editorProps={{
              handleDrop,
              handlePaste,
              handleClickOn,
            }}
            renderControls={() => (
              <EditorMenuControls
                onInsertConditional={handleInsertConditional}
              />
            )}
            RichTextFieldProps={{
              variant: "outlined",
              MenuBarProps: {},
            }}
          >
            {() => <LinkBubbleMenu />}
          </RichTextEditor>
        </Box>
        <MentionFormatPopover
          anchorEl={virtualAnchor}
          variable={popoverVariable}
          variablesByName={variables ?? {}}
          initialFormat={popoverState?.format ?? DEFAULT_FORMAT}
          initialControlledBy={popoverState?.controlledBy ?? null}
          initialOption={popoverState?.option ?? null}
          initialLinkText={popoverState?.linkText ?? null}
          onSave={handleSavePopover}
          onClose={handleClosePopover}
        />
        <ConditionalPopover
          anchorEl={conditionalAnchor}
          variablesByName={variables ?? {}}
          initialAttrs={conditionalState?.attrs ?? null}
          onSave={handleSaveConditional}
          onDelete={handleDeleteConditional}
          onClose={handleCloseConditional}
        />
      </>
    );
  }
);

export default Editor;
